import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BookmarkIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

import RichTextEditor from '../../components/diary/RichTextEditor';
import TagSelector from '../../components/forms/TagSelector';
import MoodPicker from '../../components/forms/MoodPicker';
import FolderSelector from '../../components/forms/FolderSelector';
import { diaryService } from '../../services/diary';

const EditEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [mood, setMood] = useState('');
  const [folder, setFolder] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [hasChanges, setHasChanges] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    mode: 'onChange'
  });

  const title = watch('title', '');

  // Load entry data
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const entryData = await diaryService.getEntryById(id);
        setEntry(entryData);
        setValue('title', entryData.title);
        setContent(entryData.content);
        setTags(entryData.tags);
        setMood(entryData.mood);
        setFolder(entryData.folder || '');
        setIsPinned(entryData.isPinned);
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load entry');
        navigate('/entries');
      }
    };

    fetchEntry();
  }, [id, setValue, navigate]);

  // Track changes
  useEffect(() => {
    if (entry) {
      const hasChanged = 
        title !== entry.title ||
        content !== entry.content ||
        JSON.stringify(tags) !== JSON.stringify(entry.tags) ||
        mood !== entry.mood ||
        folder !== (entry.folder || '') ||
        isPinned !== entry.isPinned;
      
      setHasChanges(hasChanged);
    }
  }, [title, content, tags, mood, folder, isPinned, entry]);

  // Auto-save functionality
  useEffect(() => {
    if (!hasChanges || !entry) return;

    const autoSave = async () => {
      setAutoSaveStatus('saving');
      try {
        await diaryService.updateEntry(id, {
          title,
          content,
          tags,
          mood,
          folder: folder || undefined,
          isPinned
        });
        setAutoSaveStatus('saved');
        setHasChanges(false);
      } catch (error) {
        setAutoSaveStatus('error');
        console.error('Auto-save failed:', error);
      }
    };

    const timer = setTimeout(autoSave, 3000);
    return () => clearTimeout(timer);
  }, [title, content, tags, mood, folder, isPinned, hasChanges, entry, id]);

  const onSubmit = async (data) => {
    if (!content.trim()) {
      toast.error('Please add some content to your entry');
      return;
    }

    setIsSubmitting(true);
    try {
      const entryData = {
        title: data.title,
        content,
        tags,
        mood,
        folder: folder || undefined,
        isPinned
      };

      await diaryService.updateEntry(id, entryData);
      toast.success('Entry updated successfully!');
      navigate(`/entries/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this entry? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await diaryService.deleteEntry(id);
      toast.success('Entry deleted successfully');
      navigate('/entries');
    } catch (error) {
      toast.error('Failed to delete entry');
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/entries/${id}`);
      }
    } else {
      navigate(`/entries/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={handleCancel}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </motion.button>
              <div>
                <h1 className="text-xl font-serif font-semibold text-ink-black">
                  Edit Entry
                </h1>
                <p className="text-sm text-gray-500">
                  Created {new Date(entry.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Auto-save status */}
              <motion.div
                className="flex items-center space-x-2 text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {autoSaveStatus === 'saving' && (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                    <span className="text-gray-500">Saving...</span>
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <CheckIcon className="h-3 w-3 text-green-500" />
                    <span className="text-gray-500">Saved</span>
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <>
                    <XMarkIcon className="h-3 w-3 text-red-500" />
                    <span className="text-red-500">Save failed</span>
                  </>
                )}
              </motion.div>

              {/* Action buttons */}
              <div className="flex items-center space-x-2">
                <motion.button
                  type="button"
                  onClick={() => setIsPinned(!isPinned)}
                  className={`p-2 rounded-lg transition-colors ${
                    isPinned
                      ? 'bg-red-100 text-red-600'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={isPinned ? 'Unpin entry' : 'Pin entry'}
                >
                  <BookmarkIcon className={`h-5 w-5 ${isPinned ? 'fill-current' : ''}`} />
                </motion.button>

                <motion.button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title="Delete entry"
                >
                  {isDeleting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                  ) : (
                    <TrashIcon className="h-5 w-5" />
                  )}
                </motion.button>

                <motion.button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>

                <motion.button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting || !title.trim()}
                  className={`btn-primary flex items-center space-x-2 ${
                    isSubmitting || !title.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={!isSubmitting && title.trim() ? { scale: 1.05 } : {}}
                  whileTap={!isSubmitting && title.trim() ? { scale: 0.95 } : {}}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <span>Update Entry</span>
                      <CheckIcon className="h-4 w-4" />
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <input
              {...register('title', {
                required: 'Title is required',
                maxLength: {
                  value: 200,
                  message: 'Title cannot exceed 200 characters'
                }
              })}
              type="text"
              placeholder="What's on your mind today?"
              className="w-full text-3xl font-serif font-bold text-ink-black placeholder-gray-400 border-none outline-none bg-transparent resize-none"
              style={{ lineHeight: '1.2' }}
            />
            {errors.title && (
              <motion.p
                className="mt-2 text-sm text-red-600"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {errors.title.message}
              </motion.p>
            )}
          </motion.div>

          {/* Metadata Row */}
          <motion.div
            className="grid md:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MoodPicker selectedMood={mood} onChange={setMood} />
            <FolderSelector selectedFolder={folder} onChange={setFolder} />
            <TagSelector selectedTags={tags} onChange={setTags} />
          </motion.div>

          {/* Content Editor */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Content
            </label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Continue writing your thoughts..."
            />
          </motion.div>

          {/* Entry History */}
          <motion.div
            className="card p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-ink-black mb-4 flex items-center space-x-2">
              <ClockIcon className="h-5 w-5" />
              <span>Entry History</span>
            </h3>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center justify-between">
                <span>Created:</span>
                <span>{new Date(entry.createdAt).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Last updated:</span>
                <span>{new Date(entry.updatedAt).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>View count:</span>
                <span>{entry.viewCount} views</span>
              </div>
              {entry.isShared && (
                <div className="flex items-center justify-between">
                  <span>Share count:</span>
                  <span>{entry.shareCount} shares</span>
                </div>
              )}
            </div>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default EditEntry;
